import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ErrorIcon from '@mui/icons-material/Error';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import Papa from 'papaparse';
import { useState } from 'react';
import userServiceInstance from '../../services/UserService';

const CSV_TEMPLATE_HEADERS = ['firstName', 'lastName', 'email', 'notificationEmail', 'userType', 'languageAccess'];

const SAMPLE_CSV_DATA = [
    CSV_TEMPLATE_HEADERS.join(','),
    'John,Doe,john@example.com,john.notifications@example.com,user,"Hindi,Gujarati"',
    'Jane,Smith,jane@example.com,,admin,"English,Spanish"', // Example with empty notification email
].join('\n');

const CsvUserImport = () => {
    const [csvFile, setCsvFile] = useState(null);
    const [parsedData, setParsedData] = useState([]);
    const [importInProgress, setImportInProgress] = useState(false);
    const [results, setResults] = useState({ success: [], failed: [] });
    const [showResults, setShowResults] = useState(false);
    const [error, setError] = useState(null);
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [helpDialogOpen, setHelpDialogOpen] = useState(false);

    // Download sample CSV template
    const handleDownloadTemplate = () => {
        const element = document.createElement('a');
        const file = new Blob([SAMPLE_CSV_DATA], { type: 'text/csv' });
        element.href = URL.createObjectURL(file);
        element.download = 'user_import_template.csv';
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
    };

    // Handle file selection
    const handleFileChange = (event) => {
        setError(null);
        setParsedData([]);
        const file = event.target.files[0];

        if (!file) return;

        // Check file type
        if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
            setError('Please select a valid CSV file');
            return;
        }

        setCsvFile(file);

        // Parse CSV file to preview data
        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: function (results) {
                // Validate CSV structure
                const requiredHeaders = ['firstName', 'lastName', 'accessLevel'];
                const missingHeaders = requiredHeaders.filter((header) => !results.meta.fields.includes(header));

                if (missingHeaders.length > 0) {
                    setError(`CSV is missing required columns: ${missingHeaders.join(', ')}`);
                    return;
                }

                // Get available languages from UserManagement component
                const languages = [
                    'English',
                    'Hindi',
                    'Gujarati',
                    'Spanish',
                    'French',
                    'German',
                    'Kannada',
                    'Mandarin',
                    'Portuguese',
                    'Korean',
                    'Russian',
                    'Telugu',
                ];

                // Pre-process each row to extract language permissions
                const processedData = results.data.map((row) => {
                    // Create language access object
                    const languageAccess = {};

                    // Extract language permissions from each column
                    languages.forEach((lang) => {
                        if (lang in row) {
                            // Check if value is a valid permission level
                            const permLevel = row[lang]?.toUpperCase();
                            if (['READ', 'WRITE', 'ADMIN'].includes(permLevel)) {
                                languageAccess[lang] = permLevel;
                            } else {
                                // Default to READ if value is invalid or empty
                                languageAccess[lang] = 'READ';
                            }

                            // Remove the language field from the row object
                            delete row[lang];
                        }
                    });

                    // Return the row with language access
                    return {
                        ...row,
                        languageAccess,
                    };
                });

                setParsedData(processedData);
            },
            error: function (error) {
                setError('Error parsing CSV file: ' + error.message);
            },
        });
    };

    // Handle import confirmation
    const handleConfirmImport = () => {
        if (parsedData.length === 0) {
            setError('No data to import. Please upload a valid CSV file.');
            return;
        }
        setOpenConfirmDialog(true);
    };

    // Handle actual import
    const handleImport = async () => {
        setOpenConfirmDialog(false);
        setImportInProgress(true);
        setShowResults(true);
        setResults({ success: [], failed: [] });

        const successfulImports = [];
        const failedImports = [];

        const authUser = JSON.parse(localStorage.getItem('authUser'));
        const authToken = localStorage.getItem('authToken');

        // Process each row sequentially
        for (let i = 0; i < parsedData.length; i++) {
            const row = parsedData[i];

            try {
                // Basic validation
                if (!row.firstName || !row.lastName || !row.accessLevel) {
                    failedImports.push({
                        ...row,
                        error: 'Missing required fields',
                    });
                    continue;
                }

                // Trim first and last names
                const firstName = row.firstName.trim();
                const lastName = row.lastName.trim();

                // Check if names are still valid after trimming
                if (!firstName || !lastName) {
                    failedImports.push({
                        ...row,
                        error: 'First name or last name contains only whitespace',
                    });
                    continue;
                }

                // Validate access level
                const accessLevel = row.accessLevel.toLowerCase();
                if (accessLevel !== 'user' && accessLevel !== 'admin' && accessLevel !== 'dubber' && accessLevel !== 'glossary_manager') {
                    failedImports.push({
                        ...row,
                        error: 'Invalid access level. Must be "user", "admin", "dubber", or "glossary_manager"',
                    });
                    continue;
                }

                // Generate email using trimmed names
                const email = `${firstName.toLowerCase()}_${lastName.toLowerCase()}@amrutvaani.org`;

                // Static password "Bapa1234"
                const password = 'Bapa1234';

                // Create user object with all required fields and trimmed names
                const newUser = {
                    firstName: firstName,
                    lastName: lastName,
                    email: email,
                    password: password,
                    userType: accessLevel,
                    notificationEmail: row.notificationEmail?.trim() || '',
                    createdBy: authUser.email,
                    updatedBy: authUser.email,
                    // Include language access permissions from the CSV
                    languageAccess: row.languageAccess || {},
                };

                // Call API to create user
                const response = await userServiceInstance.createUser(newUser, authUser.email, authToken);

                if (response.data && response.data.email) {
                    successfulImports.push({
                        ...newUser,
                    });
                } else {
                    failedImports.push({
                        ...row,
                        email,
                        error: 'API error: User creation failed',
                    });
                }
            } catch (error) {
                failedImports.push({
                    ...row,
                    error: error.response?.data?.message || error.message,
                });
            }

            // Update results as we go
            setResults({
                success: [...successfulImports],
                failed: [...failedImports],
            });
        }

        setImportInProgress(false);
    };

    const handleReset = () => {
        setCsvFile(null);
        setParsedData([]);
        setResults({ success: [], failed: [] });
        setShowResults(false);
        setError(null);

        // Clear file input
        const fileInput = document.getElementById('csv-file-input');
        if (fileInput) fileInput.value = '';
    };

    // Function to determine color scheme based on access level
    const getColorScheme = (accessLevel) => {
        switch (accessLevel) {
            case 'ADMIN':
                return { bg: 'rgba(255, 0, 0, 0.1)', color: '#f44336', border: '#f44336' }; // Red
            case 'WRITE':
                return { bg: 'rgba(255, 165, 0, 0.1)', color: '#ff9800', border: '#ff9800' }; // Orange
            case 'READ':
            default:
                return { bg: 'rgba(0, 128, 0, 0.1)', color: '#4caf50', border: '#4caf50' }; // Green
        }
    };

    const validateUsers = (users) => {
        const errors = [];

        users.forEach((user, index) => {
            if (!user.firstName) {
                errors.push(`Row ${index + 1}: First name is required`);
            }
            if (!user.lastName) {
                errors.push(`Row ${index + 1}: Last name is required`);
            }
            if (!user.email) {
                errors.push(`Row ${index + 1}: Email is required`);
            } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(user.email)) {
                errors.push(`Row ${index + 1}: Invalid email format`);
            }
            if (user.notificationEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(user.notificationEmail)) {
                errors.push(`Row ${index + 1}: Invalid notification email format`);
            }
            if (!['user', 'admin', 'dubber', 'glossary_manager'].includes(user.userType?.toLowerCase())) {
                errors.push(`Row ${index + 1}: Invalid user type (must be 'user', 'admin', 'dubber', or 'glossary_manager')`);
            }
        });

        return errors;
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h5" gutterBottom>
                    Import Users from CSV
                </Typography>
                <IconButton color="primary" onClick={() => setHelpDialogOpen(true)} size="small">
                    <HelpOutlineIcon />
                </IconButton>
            </Box>

            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                <Typography variant="body1" paragraph>
                    Upload a CSV file with columns: <strong>firstName</strong>, <strong>lastName</strong>,{' '}
                    <strong>notificationEmail</strong>, and <strong>accessLevel</strong>
                </Typography>

                <Typography variant="body2" color="text.secondary" paragraph>
                    • <strong>accessLevel</strong> must be "user", "admin", "dubber", or "glossary_manager"
                    <br />• Default password will be set to "<strong>Bapa1234</strong>" for all imported users
                    <br />• Email addresses will be automatically generated as firstName_lastName@
                    <strong>amrutvaani.org</strong> (non-editable)
                    <br />• Leading and trailing spaces will be removed from first and last names
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                    <Button
                        variant="outlined"
                        startIcon={<FileDownloadIcon />}
                        onClick={handleDownloadTemplate}
                        size="small"
                    >
                        Download Template
                    </Button>
                </Box>
            </Paper>

            {error && (
                <Alert severity="error" sx={{ my: 2 }}>
                    {error}
                </Alert>
            )}

            {!showResults ? (
                <Box sx={{ mt: 3 }}>
                    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                        <input
                            accept=".csv"
                            id="csv-file-input"
                            type="file"
                            onChange={handleFileChange}
                            style={{ display: 'none' }}
                        />
                        <label htmlFor="csv-file-input">
                            <Button component="span" variant="contained" startIcon={<CloudUploadIcon />} sx={{ mr: 2 }}>
                                Select CSV File
                            </Button>
                        </label>
                        {csvFile && (
                            <Typography variant="body2" component="span">
                                Selected: {csvFile.name}
                            </Typography>
                        )}
                    </Box>

                    {parsedData.length > 0 && (
                        <>
                            <Typography variant="h6" gutterBottom>
                                Preview ({parsedData.length} records)
                            </Typography>

                            <TableContainer component={Paper} sx={{ mb: 3 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>First Name</TableCell>
                                            <TableCell>Last Name</TableCell>
                                            <TableCell>Access Level</TableCell>
                                            <TableCell>Generated Email (non-editable)</TableCell>
                                            <TableCell>Password</TableCell>
                                            <TableCell>Language Permissions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {parsedData.slice(0, 5).map((row, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{row.firstName.trim()}</TableCell>
                                                <TableCell>{row.lastName.trim()}</TableCell>
                                                <TableCell>{row.accessLevel}</TableCell>
                                                <TableCell
                                                    sx={{
                                                        backgroundColor: (theme) =>
                                                            theme.palette.action.disabledBackground,
                                                        color: (theme) => theme.palette.text.disabled,
                                                        fontStyle: 'italic',
                                                    }}
                                                >
                                                    {`${row.firstName.trim().toLowerCase()}_${row.lastName
                                                        .trim()
                                                        .toLowerCase()}@amrutvaani.org`}
                                                </TableCell>
                                                <TableCell>Bapa1234</TableCell>
                                                <TableCell>
                                                    {/* Enhanced Language Permissions Display */}
                                                    {row.languageAccess &&
                                                    Object.keys(row.languageAccess).length > 0 ? (
                                                        <Box
                                                            sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
                                                        >
                                                            {/* First row shows most common languages */}
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    flexWrap: 'wrap',
                                                                    gap: 0.5,
                                                                    mb: 0.5,
                                                                }}
                                                            >
                                                                {['English', 'Hindi', 'Gujarati'].map((lang) =>
                                                                    row.languageAccess[lang] ? (
                                                                        <Chip
                                                                            key={lang}
                                                                            label={`${lang}: ${row.languageAccess[lang]}`}
                                                                            size="small"
                                                                            sx={{
                                                                                mr: 0.5,
                                                                                backgroundColor: getColorScheme(
                                                                                    row.languageAccess[lang],
                                                                                ).bg,
                                                                                color: getColorScheme(
                                                                                    row.languageAccess[lang],
                                                                                ).color,
                                                                                border: '1px solid',
                                                                                borderColor: getColorScheme(
                                                                                    row.languageAccess[lang],
                                                                                ).border,
                                                                                '& .MuiChip-label': {
                                                                                    px: 1,
                                                                                    fontSize: '0.7rem',
                                                                                },
                                                                            }}
                                                                        />
                                                                    ) : null,
                                                                )}
                                                            </Box>

                                                            {/* Second row shows remaining languages in a scrollable container */}
                                                            {Object.entries(row.languageAccess).filter(
                                                                ([lang]) =>
                                                                    !['English', 'Hindi', 'Gujarati'].includes(lang),
                                                            ).length > 0 && (
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        flexWrap: 'wrap',
                                                                        gap: 0.5,
                                                                        maxHeight: '60px',
                                                                        overflowY: 'auto',
                                                                        border: '1px solid #eee',
                                                                        borderRadius: '4px',
                                                                        p: 0.5,
                                                                    }}
                                                                >
                                                                    {Object.entries(row.languageAccess)
                                                                        .filter(
                                                                            ([lang]) =>
                                                                                ![
                                                                                    'English',
                                                                                    'Hindi',
                                                                                    'Gujarati',
                                                                                ].includes(lang),
                                                                        )
                                                                        .map(([lang, access]) => (
                                                                            <Chip
                                                                                key={lang}
                                                                                label={`${lang}: ${access}`}
                                                                                size="small"
                                                                                sx={{
                                                                                    mr: 0.5,
                                                                                    mb: 0.5,
                                                                                    backgroundColor:
                                                                                        getColorScheme(access).bg,
                                                                                    color: getColorScheme(access).color,
                                                                                    border: '1px solid',
                                                                                    borderColor:
                                                                                        getColorScheme(access).border,
                                                                                    '& .MuiChip-label': {
                                                                                        px: 1,
                                                                                        fontSize: '0.7rem',
                                                                                    },
                                                                                }}
                                                                            />
                                                                        ))}
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    ) : (
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                            fontStyle="italic"
                                                        >
                                                            Default (READ)
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {parsedData.length > 5 && (
                                            <TableRow>
                                                <TableCell colSpan={6} align="center">
                                                    ... and {parsedData.length - 5} more records
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleConfirmImport}
                                disabled={importInProgress}
                            >
                                Import {parsedData.length} Users
                            </Button>
                        </>
                    )}
                </Box>
            ) : (
                <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        Import Results
                    </Typography>

                    {importInProgress ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                            <CircularProgress size={24} sx={{ mr: 2 }} />
                            <Typography>
                                Importing users... ({results.success.length + results.failed.length} of{' '}
                                {parsedData.length} processed)
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <Alert severity={results.failed.length === 0 ? 'success' : 'warning'} sx={{ mb: 2 }}>
                                {results.failed.length === 0
                                    ? `Successfully imported all ${results.success.length} users!`
                                    : `Imported ${results.success.length} users with ${results.failed.length} failures.`}
                            </Alert>

                            {results.success.length > 0 && (
                                <>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                                        <Typography variant="subtitle1">
                                            Successful Imports ({results.success.length})
                                        </Typography>
                                    </Box>

                                    <TableContainer component={Paper} sx={{ mb: 3 }}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Name</TableCell>
                                                    <TableCell>Email</TableCell>
                                                    <TableCell>Password</TableCell>
                                                    <TableCell>Access Level</TableCell>
                                                    <TableCell>Language Permissions</TableCell> {/* Added column */}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {results.success.map((user, index) => (
                                                    <TableRow key={index}>
                                                        <TableCell>
                                                            {user.firstName} {user.lastName}
                                                        </TableCell>
                                                        <TableCell>{user.email}</TableCell>
                                                        <TableCell>Bapa1234</TableCell>
                                                        <TableCell>{user.userType}</TableCell>
                                                        <TableCell>
                                                            {/* Enhanced language permissions display */}
                                                            {user.languageAccess &&
                                                            Object.keys(user.languageAccess).length > 0 ? (
                                                                <Box sx={{ maxHeight: '80px', overflowY: 'auto' }}>
                                                                    <Box
                                                                        sx={{
                                                                            display: 'flex',
                                                                            flexWrap: 'wrap',
                                                                            gap: 0.5,
                                                                        }}
                                                                    >
                                                                        {Object.entries(user.languageAccess).map(
                                                                            ([lang, access]) => (
                                                                                <Chip
                                                                                    key={lang}
                                                                                    label={`${lang}: ${access}`}
                                                                                    size="small"
                                                                                    sx={{
                                                                                        mr: 0.5,
                                                                                        mb: 0.5,
                                                                                        backgroundColor:
                                                                                            getColorScheme(access).bg,
                                                                                        color: getColorScheme(access)
                                                                                            .color,
                                                                                        border: '1px solid',
                                                                                        borderColor:
                                                                                            getColorScheme(access)
                                                                                                .border,
                                                                                        '& .MuiChip-label': {
                                                                                            px: 1,
                                                                                            fontSize: '0.7rem',
                                                                                        },
                                                                                    }}
                                                                                />
                                                                            ),
                                                                        )}
                                                                    </Box>
                                                                </Box>
                                                            ) : (
                                                                <Typography
                                                                    variant="body2"
                                                                    color="text.secondary"
                                                                    fontStyle="italic"
                                                                >
                                                                    Default (READ)
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </>
                            )}

                            {results.failed.length > 0 && (
                                <>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <ErrorIcon color="error" sx={{ mr: 1 }} />
                                        <Typography variant="subtitle1">
                                            Failed Imports ({results.failed.length})
                                        </Typography>
                                    </Box>

                                    <TableContainer component={Paper} sx={{ mb: 3 }}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Name</TableCell>
                                                    <TableCell>Generated Email</TableCell>
                                                    <TableCell>Error</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {results.failed.map((row, index) => (
                                                    <TableRow key={index}>
                                                        <TableCell>
                                                            {row.firstName} {row.lastName}
                                                        </TableCell>
                                                        <TableCell>
                                                            {row.email ||
                                                                `${row.firstName?.toLowerCase()}_${row.lastName?.toLowerCase()}@amrutvaani.org`}
                                                        </TableCell>
                                                        <TableCell>{row.error}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </>
                            )}

                            <Button variant="outlined" onClick={handleReset}>
                                Import Another File
                            </Button>
                        </>
                    )}
                </Box>
            )}

            {/* Confirmation Dialog */}
            <Dialog open={openConfirmDialog} onClose={() => setOpenConfirmDialog(false)}>
                <DialogTitle>Confirm User Import</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        You are about to import {parsedData.length} users. Each user will be created with the password
                        "Bapa1234".
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenConfirmDialog(false)}>Cancel</Button>
                    <Button onClick={handleImport} color="primary" variant="contained">
                        Proceed with Import
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Help Dialog */}
            <Dialog open={helpDialogOpen} onClose={() => setHelpDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    CSV Import Help
                    <IconButton
                        aria-label="close"
                        onClick={() => setHelpDialogOpen(false)}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="h6" gutterBottom>
                        Required CSV Format
                    </Typography>
                    <Typography variant="body2" paragraph>
                        Your CSV file must include these columns:
                    </Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Column Name</TableCell>
                                    <TableCell>Description</TableCell>
                                    <TableCell>Examples</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow>
                                    <TableCell>
                                        <strong>firstName</strong>
                                    </TableCell>
                                    <TableCell>User's first name</TableCell>
                                    <TableCell>John, Sarah</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>
                                        <strong>lastName</strong>
                                    </TableCell>
                                    <TableCell>User's last name</TableCell>
                                    <TableCell>Smith, Patel</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>
                                        <strong>accessLevel</strong>
                                    </TableCell>
                                    <TableCell>Primary role: "user", "admin", "dubber", or "glossary_manager"</TableCell>
                                    <TableCell>user, admin, dubber, glossary_manager</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>
                                        <strong>Language Columns</strong>
                                    </TableCell>
                                    <TableCell>
                                        One column per language (English, Hindi, etc.) with READ, WRITE or ADMIN value
                                    </TableCell>
                                    <TableCell>READ, WRITE, ADMIN</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Typography variant="h6" gutterBottom>
                        Sample CSV Content
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.100' }}>
                        <code>
                            firstName,lastName,accessLevel,English,Hindi,Gujarati
                            <br />
                            John,Smith,user,READ,READ,READ
                            <br />
                            Sarah,Johnson,admin,ADMIN,WRITE,WRITE
                            <br />
                            Kumar,Patel,user,WRITE,READ,ADMIN
                        </code>
                    </Paper>

                    <Typography variant="h6" gutterBottom>
                        Notes
                    </Typography>
                    <Typography variant="body2">
                        • Email addresses will be automatically generated as firstName_lastName@amrutvaani.org
                        (non-editable)
                        <br />
                        • First and last names will have leading and trailing spaces removed
                        <br />
                        • All users will be created with the default password "Bapa1234"
                        <br />
                        • Users should be instructed to change their password after first login
                        <br />
                        • For language columns, use READ, WRITE, or ADMIN (defaults to READ if omitted)
                        <br />
                        • The CSV file should use comma as the delimiter
                        <br />• Header row is required
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setHelpDialogOpen(false)}>Close</Button>
                    <Button
                        variant="outlined"
                        startIcon={<FileDownloadIcon />}
                        onClick={() => {
                            handleDownloadTemplate();
                            setHelpDialogOpen(false);
                        }}
                    >
                        Download Template
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CsvUserImport;
